/**
 * Privacy Policy Page
 *
 * Displays the Privacy Policy for Arber.
 * Effective Date: December 16, 2025
 */

import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import '../styles/LegalPage.css';

export default function PrivacyPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="legal-page">
      {/* Navigation Header */}
      <nav className="legal-nav">
        <div className="legal-nav-content">
          <Link to="/" className="legal-brand">
            <img src="/logo.png" alt="Arber Logo" className="legal-logo" />
            <span className="legal-brand-text">Arber</span>
          </Link>
          <div className="legal-nav-links">
            <Link to="/">Home</Link>
            <Link to="/terms">Terms of Service</Link>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="legal-content">
        <div className="legal-container">
          <h1 className="legal-title">Privacy Policy</h1>
          <p className="legal-updated">Effective Date: December 16, 2025</p>

          <section className="legal-section">
            <h2>1. Introduction</h2>
            <p>
              Arber ("we," "us," or "our") respects your privacy and is committed to protecting your
              personal data. This Privacy Policy explains how we collect, use, and share information
              about you when you use our website <a href="https://arber.bet" target="_blank" rel="noopener noreferrer">arber.bet</a> (the
              "Service").
            </p>
            <p>
              This policy is designed to comply with the General Data Protection Regulation (GDPR),
              the California Consumer Privacy Act (CCPA), and other applicable privacy laws.
            </p>
          </section>

          <section className="legal-section">
            <h2>2. Information We Collect</h2>
            <p>We collect information to provide our analytics services and manage your subscription.</p>

            <h3>2.1 Information You Provide to Us</h3>
            <ul>
              <li><strong>Account Data:</strong> When you register, we collect your <strong>email address</strong> and <strong>password</strong>. Optionally, you may provide your <strong>full name</strong> and an <strong>avatar URL</strong>.</li>
              <li><strong>Support Data:</strong> Any information you provide when you contact our support team (e.g., feedback, bug reports).</li>
            </ul>

            <h3>2.2 Information Collected Automatically</h3>
            <ul>
              <li><strong>Usage Data:</strong> We track your interaction with the platform, including the number of arbitrage, +EV, and player prop searches you perform daily to enforce subscription limits.</li>
              <li><strong>Technical Data:</strong> We collect your IP address (stored temporarily in server memory for rate limiting) and subscription status.</li>
              <li><strong>Session Data:</strong> We use JSON Web Tokens (JWT) stored in your browser's <code>sessionStorage</code> to maintain your login session.</li>
            </ul>

            <h3>2.3 Payment Information</h3>
            <p>
              We use <strong>Stripe</strong> to process payments. Arber <strong>does not</strong> collect
              or store your full credit card number, expiration date, or CVV. Stripe provides us with a
              Customer ID and Subscription ID to manage your account access.
            </p>
          </section>

          <section className="legal-section">
            <h2>3. How We Use Your Information</h2>
            <p>We use your data for the following purposes:</p>
            <ol>
              <li><strong>Service Delivery:</strong> To authenticate you, allow access to features (Arbitrage, +EV), and display personalized data.</li>
              <li><strong>Billing:</strong> To process subscriptions and manage auto-renewals via Stripe.</li>
              <li><strong>Enforcement:</strong> To track search usage and enforce the limits of your Free, Gold, or Platinum tier.</li>
              <li><strong>Communication:</strong> To send you administrative emails (e.g., password resets, policy updates).</li>
              <li><strong>Security:</strong> To detect and prevent fraud, abuse (e.g., botting), and security incidents.</li>
            </ol>
          </section>

          <section className="legal-section">
            <h2>4. How We Share Your Information</h2>
            <p>
              We do not sell your personal data. We share data only with the following third-party
              service providers necessary to operate our business:
            </p>

            <div className="legal-table-container">
              <table className="legal-table">
                <thead>
                  <tr>
                    <th>Service Provider</th>
                    <th>Purpose</th>
                    <th>Data Shared</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>Supabase</strong></td>
                    <td>Database & Authentication</td>
                    <td>Email, Hashed Password, Profile Data, Usage Logs</td>
                  </tr>
                  <tr>
                    <td><strong>Stripe</strong></td>
                    <td>Payment Processing</td>
                    <td>Email, Name, Payment Details (Directly entered into Stripe)</td>
                  </tr>
                  <tr>
                    <td><strong>Vercel</strong></td>
                    <td>Web Hosting</td>
                    <td>IP Address (Standard web traffic logs)</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p>
              <strong>Note on "The Odds API":</strong> We consume sports data from this provider, but we
              do not share any of your personal user data with them.
            </p>
          </section>

          <section className="legal-section">
            <h2>5. Data Retention</h2>
            <p>We retain your personal data only as long as necessary:</p>
            <ul>
              <li><strong>Account Data:</strong> Retained until you request account deletion.</li>
              <li><strong>Usage Data (Search Counts):</strong> Retained for <strong>90 days</strong> to manage daily limits and analyze trends.</li>
              <li><strong>Subscription History:</strong> Retained indefinitely for tax and audit purposes.</li>
              <li><strong>Session Tokens:</strong> Automatically expire after 1 hour; stored locally on your device.</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>6. Security Measures</h2>
            <p>We implement robust security measures to protect your data, including:</p>
            <ul>
              <li><strong>Encryption:</strong> All data is transmitted via HTTPS. Passwords are hashed using industry-standard algorithms (bcrypt) before storage.</li>
              <li><strong>Row-Level Security (RLS):</strong> Our database ensures users can only access their own data.</li>
              <li><strong>Access Control:</strong> JWT-based authentication with 1-hour expiration.</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>7. Your Rights (GDPR & CCPA)</h2>
            <p>Depending on your location, you have specific rights regarding your data.</p>

            <h3>7.1 For Users in the European Union (GDPR) and UK</h3>
            <ul>
              <li><strong>Right to Access:</strong> You can request a copy of the personal data we hold about you.</li>
              <li><strong>Right to Rectification:</strong> You can update your email or name in your account settings.</li>
              <li><strong>Right to Erasure:</strong> You can request that we delete your account and associated data.</li>
              <li><strong>Right to Portability:</strong> You can request your data in a structured, machine-readable format.</li>
              <li><strong>Right to Withdraw Consent:</strong> You may withdraw consent for processing at any time (which may require account termination).</li>
            </ul>

            <h3>7.2 For Users in California (CCPA)</h3>
            <ul>
              <li><strong>Right to Know:</strong> You may request details about the categories of personal information we collect.</li>
              <li><strong>Right to Delete:</strong> You may request the deletion of your personal information.</li>
              <li><strong>Right to Non-Discrimination:</strong> We will not discriminate against you (e.g., by denying services) for exercising your privacy rights.</li>
              <li><strong>Do Not Sell:</strong> Arber does not sell personal information.</li>
            </ul>

            <h3>7.3 How to Exercise Your Rights</h3>
            <p>
              To exercise any of these rights, please email us at <a href="mailto:arberodds@gmail.com">arberodds@gmail.com</a>.
              We will respond to verified requests within 30 days.
            </p>
          </section>

          <section className="legal-section">
            <h2>8. International Data Transfers</h2>
            <p>
              Arber is operated from the United States. If you are accessing the Service from the EU,
              UK, or Australia, please be aware that your information may be transferred to, stored,
              and processed in the United States and other countries where our third-party providers
              (Supabase, Stripe) operate. By using the Service, you consent to this transfer. We rely
              on standard contractual clauses or adequacy decisions where applicable for data transfers.
            </p>
          </section>

          <section className="legal-section">
            <h2>9. Children's Privacy</h2>
            <p>
              Our Service is strictly for users aged <strong>21 and over</strong>. We do not knowingly
              collect personal identifiable information from anyone under the age of 21. If we discover
              that a user under 21 has provided us with personal information, we will immediately delete it.
            </p>
          </section>

          <section className="legal-section">
            <h2>10. Cookies and Tracking</h2>
            <p>We do not use third-party advertising cookies or marketing pixels.</p>
            <ul>
              <li><strong>Authentication:</strong> We use <code>sessionStorage</code> in your browser to store your login token. This is essential for the website to function.</li>
              <li><strong>Analytics:</strong> We track usage (e.g., search counts) server-side and do not place analytics cookies on your device.</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>11. Changes to This Privacy Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of any changes
              by posting the new Privacy Policy on this page and updating the "Effective Date." You
              are advised to review this Privacy Policy periodically for any changes.
            </p>
          </section>

          <section className="legal-section">
            <h2>12. Contact Us</h2>
            <p>If you have any questions about this Privacy Policy, please contact us:</p>
            <ul>
              <li><strong>Email:</strong> <a href="mailto:arberodds@gmail.com">arberodds@gmail.com</a></li>
              <li><strong>Website:</strong> <a href="https://arber.bet" target="_blank" rel="noopener noreferrer">https://arber.bet</a></li>
            </ul>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="legal-footer">
        <div className="legal-footer-content">
          <p>&copy; 2025 Arber. All rights reserved.</p>
          <div className="legal-footer-links">
            <Link to="/terms">Terms of Service</Link>
            <Link to="/privacy">Privacy Policy</Link>
            <a href="mailto:arberodds@gmail.com">Contact Us</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
